import { useMemo } from "react";
import { useDealsListStore } from "@/store/deals-list-store";

/**
 * Hook to check if a deal has any report currently in progress.
 *
 * @param dealId - Optional. When provided, only checks that specific deal.
 *                 When omitted, checks ALL deals in the workspace (legacy behavior).
 * @returns true if any report status is "in_progress" or starts with "generating_".
 */
export const useHasInProgressReports = (dealId?: string) => {
  const deals = useDealsListStore((state) => state.deals);

  return useMemo(() => {
    // Helper to check if a single deal has in-progress reports
    const checkDealInProgress = (deal: (typeof deals)[string]): boolean => {
      const reportStatus = deal.reportStatus?.opportunityApplication?.report_status;
      if (!reportStatus) return false;

      const statusValues = Object.values(reportStatus);
      return statusValues.some((status) => {
        if (typeof status !== "string") return false;
        const statusLower = status.toLowerCase();
        return (
          status === "in_progress" ||
          statusLower === "in_progress" ||
          status === "generating_main_webpage_info" ||
          status === "generating_secondary_webpage_info" ||
          statusLower.startsWith("generating_")
        );
      });
    };

    // If dealId is provided, only check that specific deal
    if (dealId) {
      const deal = deals[dealId];
      if (!deal) return false;
      return checkDealInProgress(deal);
    }

    // Fallback: check all deals (legacy behavior for backwards compatibility)
    const dealList = Object.values(deals);
    for (const deal of dealList) {
      if (checkDealInProgress(deal)) {
        return true;
      }
    }

    return false;
  }, [deals, dealId]);
};
